/**
 * Player availability for tournament scheduling.
 * Slots are identified by weekday (1=Mon..7=Sun) + time "HH:MM".
 * e.g. "1_18:00" = Monday 6pm, "3_19:30" = Wednesday 7:30pm
 * All Firestore access goes via Cloud Functions (availabilityApi).
 */

import * as availabilityApi from '../api/availabilityApi';
import { toUtcMs } from '../utils/dateTimeUtils';

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function parseTime(s) {
  if (!s || typeof s !== 'string') return 18 * 60;
  const parts = s.trim().split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
}

function formatTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Generate slot keys (weekday_time) for a schedule config.
 * @param {object} config - scheduleConfig with weekdays, scheduleStart, scheduleEnd, matchDuration
 * @returns {string[]} slot keys e.g. ["1_18:00", "1_18:30", "3_18:00"]
 */
export function generateSlotKeys(config) {
  const weekdays = config?.weekdays || [1, 3, 5];
  const duration = Math.max(15, config?.matchDuration || 30);
  const startMins = parseTime(config?.scheduleStart ?? '18:00');
  const endMins = parseTime(config?.scheduleEnd ?? '21:00');
  const span = endMins > startMins ? endMins - startMins : 24 * 60 - startMins + endMins;
  const slotsPerDay = Math.max(1, Math.floor(span / duration));

  const keys = [];
  for (const wd of weekdays) {
    for (let i = 0; i < slotsPerDay; i++) {
      const timeMins = startMins + i * duration;
      keys.push(`${wd}_${formatTime(timeMins)}`);
    }
  }
  return keys;
}

/**
 * Format a slot key for display.
 */
export function formatSlotKey(key) {
  const [wd, time] = (key || '').split('_');
  const day = DAY_NAMES[parseInt(wd, 10)] || wd;
  return `${day} ${time || ''}`;
}

/**
 * Format tournament schedule config as a short summary (e.g. "Mon, Wed, Fri 18:00–21:00").
 * Use when showing "all tournament slots" for selectAll availability.
 */
export function formatScheduleSummary(config) {
  if (!config) return '18:00–21:00';
  const weekdays = config.weekdays || [1, 3, 5];
  const start = (config.scheduleStart || '18:00').toString().trim();
  const end = (config.scheduleEnd || '21:00').toString().trim();
  const dayNames = weekdays
    .filter((wd) => wd >= 1 && wd <= 7)
    .sort((a, b) => a - b)
    .map((wd) => DAY_NAMES[wd])
    .filter(Boolean);
  const dayStr = dayNames.length > 0 ? dayNames.join(', ') : '—';
  return `${dayStr} ${start}–${end}`;
}

/**
 * Load player availability for a tournament. Via Cloud Functions.
 * @returns {Promise<{ selectAll: boolean, availableSlots: string[], exclusionDates: string[], holidayDates: string[], googleCalendarConnected: boolean }|null>}
 */
export async function getPlayerAvailability(tournamentId, playerId) {
  return availabilityApi.getPlayerAvailability(tournamentId, playerId);
}

/**
 * Save player availability. Via Cloud Functions.
 */
export async function savePlayerAvailability(tournamentId, playerId, data) {
  await availabilityApi.savePlayerAvailability(tournamentId, playerId, data);
}

/**
 * Add a date (YYYY-MM-DD) to a player's exclusion dates for a tournament.
 * Used when a player declines the calendar invite so we mark them unavailable for that day.
 * Pass matchId when caller has match access (e.g. calendar decline flow).
 */
export async function addExclusionDateForPlayer(tournamentId, playerId, dateKey, matchId) {
  await availabilityApi.addExclusionDateForPlayer(tournamentId, playerId, dateKey, matchId);
}

/**
 * Load all availability records for a player across all tournaments. Via Cloud Functions.
 * @returns {Promise<Array<{ tournamentId: string, selectAll: boolean, availableSlots: string[], exclusionDates: string[], holidayDates: string[] }>>}
 */
export async function getAllPlayerAvailability(playerId) {
  if (!playerId) return [];
  return availabilityApi.getAllPlayerAvailability(playerId);
}

/**
 * Summarize availability slots across multiple records (e.g. for display).
 * Returns union of slots, grouped by weekday.
 */
export function summarizeAvailabilitySlots(availabilityList) {
  const allSlots = new Set();
  let anySelectAll = false;
  for (const a of availabilityList || []) {
    if (a.selectAll) anySelectAll = true;
    (a.availableSlots || []).forEach((s) => allSlots.add(s));
  }
  if (anySelectAll && availabilityList?.length > 0) return { selectAll: true, slotKeys: [], summary: 'All slots (in some tournaments)' };
  const byDay = {};
  for (const key of allSlots) {
    const [wd, time] = (key || '').split('_');
    const day = DAY_NAMES[parseInt(wd, 10)] || `Day ${wd}`;
    if (!byDay[day]) byDay[day] = new Set();
    byDay[day].add(time || '');
  }
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const sorted = dayNames
    .filter((d) => byDay[d])
    .map((d) => `${d} ${[...byDay[d]].sort().join(', ')}`);
  return { selectAll: false, slotKeys: [...allSlots], summary: sorted.length ? sorted.join(' · ') : 'None' };
}

/**
 * Load availability for multiple players in a tournament. Via Cloud Functions.
 * @returns {Promise<Map<string, object>>} playerId -> availability
 */
export async function getAvailabilityForPlayers(tournamentId, playerIds) {
  return availabilityApi.getAvailabilityForPlayers(tournamentId, playerIds);
}

/**
 * Check if a slot (weekday, time) is available for a player given their availability.
 * @param {string} slotKey - e.g. "1_18:00"
 * @param {object} availability - from getPlayerAvailability
 * @param {string} dateKey - YYYY-MM-DD for exclusion/holiday check
 */
export function isSlotAvailableForPlayer(slotKey, availability, dateKey) {
  if (!availability) return true; // no data = assume available
  if (availability.exclusionDates?.includes(dateKey)) return false;
  if (availability.holidayDates?.includes(dateKey)) return false;
  if (availability.selectAll) return true;
  return (availability.availableSlots || []).includes(slotKey);
}

/**
 * Get reason a slot is unavailable for a player (for audit). Returns null if available.
 * @param {string} slotKey - e.g. "1_18:00"
 * @param {object} availability - from getPlayerAvailability
 * @param {string} dateKey - YYYY-MM-DD for exclusion/holiday check
 * @returns {null|'exclusion_date'|'holiday_date'|'time_slot'}
 */
export function getUnavailabilityReason(slotKey, availability, dateKey) {
  if (!availability) return null;
  if (availability.exclusionDates?.includes(dateKey)) return 'exclusion_date';
  if (availability.holidayDates?.includes(dateKey)) return 'holiday_date';
  if (availability.selectAll) return null;
  if (!(availability.availableSlots || []).includes(slotKey)) return 'time_slot';
  return null;
}

/**
 * Get YYYY-MM-DD (local date) from any date-like value for holiday/exclusion matching.
 * Handles Date, Firestore Timestamp, ISO string, or ms number.
 */
export function availabilityDateKey(dateLike) {
  const ms = toUtcMs(dateLike);
  if (ms == null || isNaN(ms)) return '';
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function toDateKey(dateLike) {
  return availabilityDateKey(dateLike);
}

/**
 * Create an availability checker for scheduleStrategy.
 * @param {Map<string, object>} availabilityByPlayerId - playerId -> availability from getPlayerAvailability
 * @returns {(slot: {date, time, weekday}, participantIds: string[]) => boolean}
 */
export function createAvailabilityChecker(availabilityByPlayerId) {
  return (slot, participantIds) => {
    if (!availabilityByPlayerId || participantIds.length === 0) return true;
    const dateKey = slot?.date ? toDateKey(slot.date) : '';
    const slotKey = slot ? `${slot.weekday}_${slot.time}` : '';
    for (const pid of participantIds) {
      const avail = availabilityByPlayerId.get(pid);
      if (!isSlotAvailableForPlayer(slotKey, avail, dateKey)) return false;
    }
    return true;
  };
}
